// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import type { ImportResult, ImportedTransaction, ImportedAccount, ImportError, ImportWarning } from './types.js';
import { getCurrencyDecimalPlaces } from '../currencies.js';

// ---------------------------------------------------------------------------
// OFX date parsing
// Format: YYYYMMDDHHMMSS.mmm[±H:TZ] or YYYYMMDD
// ---------------------------------------------------------------------------

export function parseOfxDate(raw: string): string | null {
  const s = raw.trim();
  // Strip timezone suffix: [+5:EST] or [-5:EST]
  const base = s.replace(/\[.*\]$/, '').split('.')[0] ?? '';
  if (base.length < 8) return null;
  const year = base.slice(0, 4);
  const month = base.slice(4, 6);
  const day = base.slice(6, 8);
  if (!year || !month || !day) return null;
  return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// OFX SGML parser
// OFX SGML is not valid XML — it has unclosed tags.
// We extract values with a simple regex-based tag extractor.
// ---------------------------------------------------------------------------

function extractSgmlValue(content: string, tag: string): string | null {
  const re = new RegExp(`<${tag}>([^<]*)`, 'i');
  const m = content.match(re);
  return m ? m[1]!.trim() : null;
}

function extractSgmlBlocks(content: string, tag: string): string[] {
  const openTag = `<${tag}>`;
  const closeTag = `</${tag}>`;
  const blocks: string[] = [];
  let pos = 0;
  while (true) {
    const start = content.indexOf(openTag, pos);
    if (start === -1) break;
    const end = content.indexOf(closeTag, start + openTag.length);
    if (end === -1) {
      // SGML OFX often omits close tags — capture to next same-level open or EOF
      blocks.push(content.slice(start + openTag.length));
      break;
    }
    blocks.push(content.slice(start + openTag.length, end));
    pos = end + closeTag.length;
  }
  return blocks;
}

function parseSgmlOFX(content: string): OFXData {
  const errors: ImportError[] = [];
  const warnings: ImportWarning[] = [];

  // Strip OFX header block (lines before <OFX>)
  const ofxStart = content.indexOf('<OFX>');
  const body = ofxStart >= 0 ? content.slice(ofxStart) : content;

  const currency = extractSgmlValue(body, 'CURDEF') ?? 'USD';

  // Account info
  const bankAcct = extractSgmlBlocks(body, 'BANKACCTFROM')[0] ?? '';
  const ccAcct = extractSgmlBlocks(body, 'CCACCTFROM')[0] ?? '';
  const acctId = extractSgmlValue(bankAcct || ccAcct, 'ACCTID') ?? '';
  const bankId = extractSgmlValue(bankAcct, 'BANKID') ?? '';
  const acctType = extractSgmlValue(bankAcct, 'ACCTTYPE') ?? (ccAcct ? 'CREDITCARD' : '');

  // Transactions
  const transactions: OFXTransaction[] = [];
  const tranList = extractSgmlBlocks(body, 'BANKTRANLIST')[0]
    ?? extractSgmlBlocks(body, 'INVBANKTRANLIST')[0]
    ?? body;

  const stmtTrns = extractSgmlBlocks(tranList, 'STMTTRN');
  for (const trn of stmtTrns) {
    const fitid = extractSgmlValue(trn, 'FITID') ?? '';
    const dtPosted = extractSgmlValue(trn, 'DTPOSTED') ?? '';
    const amount = extractSgmlValue(trn, 'TRNAMT') ?? '';
    const name = extractSgmlValue(trn, 'NAME') ?? extractSgmlValue(trn, 'MEMO') ?? '';
    const memo = extractSgmlValue(trn, 'MEMO') ?? '';
    const checknum = extractSgmlValue(trn, 'CHECKNUM') ?? '';
    const trntype = extractSgmlValue(trn, 'TRNTYPE') ?? '';

    const date = parseOfxDate(dtPosted);
    if (!date) {
      errors.push({ message: `Cannot parse OFX date: "${dtPosted}"` });
      continue;
    }
    const curDecimals = getCurrencyDecimalPlaces(currency);
    const amtCents = Math.round(parseFloat(amount) * Math.pow(10, curDecimals));
    if (isNaN(amtCents)) {
      errors.push({ message: `Cannot parse OFX amount: "${amount}"` });
      continue;
    }

    transactions.push({ fitid, date, amountCents: amtCents, name, memo, checknum, trntype });
  }

  // Closing balance
  const ledgerBal = extractSgmlBlocks(body, 'LEDGERBAL')[0] ?? '';
  const balAmt = extractSgmlValue(ledgerBal, 'BALAMT');
  const balDate = extractSgmlValue(ledgerBal, 'DTASOF');
  const closingBalance = balAmt ? Math.round(parseFloat(balAmt) * Math.pow(10, getCurrencyDecimalPlaces(currency))) : undefined;
  const closingBalanceDate = balDate ? parseOfxDate(balDate) ?? undefined : undefined;

  const data: OFXData = {
    currency, acctId, bankId, acctType, transactions,
    closingBalance, closingBalanceDate, errors, warnings,
  };
  return data;
}

// ---------------------------------------------------------------------------
// OFX XML parser (version 2.x)
// ---------------------------------------------------------------------------

function parseXmlOFX(content: string): OFXData {
  const errors: ImportError[] = [];
  const warnings: ImportWarning[] = [];

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(content, 'application/xml');
  } catch (e) {
    errors.push({ message: `XML parse error: ${String(e)}` });
    return { currency: 'USD', acctId: '', bankId: '', acctType: '', transactions: [], errors, warnings, closingBalance: undefined, closingBalanceDate: undefined };
  }

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    errors.push({ message: `XML parse error: ${parseError.textContent ?? ''}` });
    return { currency: 'USD', acctId: '', bankId: '', acctType: '', transactions: [], errors, warnings, closingBalance: undefined, closingBalanceDate: undefined };
  }

  const getText = (parent: Element | Document, tag: string): string => {
    return parent.querySelector(tag)?.textContent?.trim() ?? '';
  };

  const currency = getText(doc, 'CURDEF') || 'USD';
  const acctId = getText(doc, 'ACCTID');
  const bankId = getText(doc, 'BANKID');
  const acctType = getText(doc, 'ACCTTYPE') || (doc.querySelector('CCACCTFROM') ? 'CREDITCARD' : '');

  const transactions: OFXTransaction[] = [];
  const trns = doc.querySelectorAll('STMTTRN');
  for (const trn of Array.from(trns)) {
    const fitid = getText(trn, 'FITID');
    const dtPosted = getText(trn, 'DTPOSTED');
    const amount = getText(trn, 'TRNAMT');
    const name = getText(trn, 'NAME') || getText(trn, 'MEMO');
    const memo = getText(trn, 'MEMO');
    const checknum = getText(trn, 'CHECKNUM');
    const trntype = getText(trn, 'TRNTYPE');

    const date = parseOfxDate(dtPosted);
    if (!date) {
      errors.push({ message: `Cannot parse OFX date: "${dtPosted}"` });
      continue;
    }
    const curDecimals = getCurrencyDecimalPlaces(currency);
    const amtCents = Math.round(parseFloat(amount) * Math.pow(10, curDecimals));
    if (isNaN(amtCents)) {
      errors.push({ message: `Cannot parse OFX amount: "${amount}"` });
      continue;
    }

    transactions.push({ fitid, date, amountCents: amtCents, name, memo, checknum, trntype });
  }

  const balAmt = getText(doc, 'BALAMT');
  const balDate = getText(doc, 'DTASOF');
  const closingBalance = balAmt ? Math.round(parseFloat(balAmt) * Math.pow(10, getCurrencyDecimalPlaces(currency))) : undefined;
  const closingBalanceDate = balDate ? parseOfxDate(balDate) ?? undefined : undefined;

  const data: OFXData = {
    currency, acctId, bankId, acctType, transactions,
    closingBalance, closingBalanceDate, errors, warnings,
  };
  return data;
}

// ---------------------------------------------------------------------------
// Format detection
// ---------------------------------------------------------------------------

export function detectOfxFormat(content: string): 'sgml' | 'xml' {
  const head = content.trimStart().slice(0, 100);
  if (head.startsWith('<?xml') || head.startsWith('<OFX') || head.startsWith('<ofx')) return 'xml';
  return 'sgml';
}

// ---------------------------------------------------------------------------
// Internal OFX data structure
// ---------------------------------------------------------------------------

interface OFXTransaction {
  fitid: string;
  date: string;
  amountCents: number;
  name: string;
  memo: string;
  checknum: string;
  trntype: string;
}

interface OFXData {
  currency: string;
  acctId: string;
  bankId: string;
  acctType: string;
  transactions: OFXTransaction[];
  closingBalance: number | undefined;
  closingBalanceDate: string | undefined;
  errors: ImportError[];
  warnings: ImportWarning[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface OfxParseOptions {
  accountId: string;
}

export function parseOfx(content: string, options: OfxParseOptions): ImportResult {
  const fmt = detectOfxFormat(content);
  const data = fmt === 'xml' ? parseXmlOFX(content) : parseSgmlOFX(content);

  const transactions: ImportedTransaction[] = [];
  const accounts: ImportedAccount[] = [];

  if (data.acctId) {
    accounts.push({
      externalId: data.acctId,
      name: `${data.acctType || 'Account'} ...${data.acctId.slice(-4)}`,
      type: data.acctType,
      currency: data.currency,
    });
  }

  for (const trn of data.transactions) {
    transactions.push({
      date: trn.date,
      description: trn.name,
      amount: trn.amountCents,
      currency: data.currency,
      accountId: options.accountId,
      ...(trn.fitid ? { fitid: trn.fitid } : {}),
      ...(trn.memo && trn.memo !== trn.name ? { memo: trn.memo } : {}),
      ...(trn.checknum ? { num: trn.checknum } : {}),
      rawData: { fitid: trn.fitid, trntype: trn.trntype },
    });
  }

  const dates = transactions.map((t) => t.date).sort();
  return {
    transactions, accounts, errors: data.errors, warnings: data.warnings,
    format: 'ofx',
    dateRange: { start: dates[0] ?? '', end: dates[dates.length - 1] ?? '' },
    totalTransactions: transactions.length,
    ...(data.closingBalance !== undefined ? { closingBalance: data.closingBalance } : {}),
    ...(data.closingBalanceDate !== undefined ? { closingBalanceDate: data.closingBalanceDate } : {}),
  };
}
